import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import CountdownTimer from "@/components/CountdownTimer";
import MapEmbed from "@/components/MapEmbed";
import RSVPFormRenderer from "@/components/RSVPFormRenderer";
import { PALETTES } from "@/lib/eventOptions";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Lock, BookOpen } from "lucide-react";
import { themeMotifConfig, ThemeOrnament } from "@/lib/themeMotif";
import ThemeBand from "@/components/ThemeBand";
import ThemeBandImage from "@/components/ThemeBandImage";
import { publicImageUrl } from "@/components/ImageUpload";

export default function PublicInvite() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const refToken = params.get("ref");
  const [event, setEvent] = useState(null);
  const [errState, setErrState] = useState(null);
  const [referrerName, setReferrerName] = useState(null);

  useEffect(() => {
    api.get(`/public/events/${slug}`)
      .then((r) => setEvent(r.data))
      .catch((err) => {
        if (err.response?.status === 403) setErrState("locked");
        else setErrState("notfound");
      });
  }, [slug]);

  useEffect(() => {
    if (!refToken || !slug) return;
    api.get(`/public/events/${slug}/referrer?ref=${encodeURIComponent(refToken)}`)
      .then((r) => setReferrerName(r.data?.name || null))
      .catch(() => {});
  }, [slug, refToken]);

  if (errState === "locked") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF9F4] px-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
        <div className="max-w-lg text-center">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-neutral-500 border border-black/10 px-3 py-2 mb-8">
            <Lock size={12} /> Invitation locked
          </div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl sm:text-6xl font-light tracking-tight">
            This invite is on hold.
          </h1>
          <p className="mt-5 text-neutral-600 leading-relaxed">
            Your host is still finishing their preparations. This shareable link will unlock once the invitation is published.
            If you&apos;re expecting to attend, please check back shortly — or ask your host directly.
          </p>
          <Link to="/" className="mt-8 inline-block text-xs tracking-[0.25em] uppercase underline underline-offset-4 text-neutral-700 hover:text-black">
            Learn about Soirée
          </Link>
        </div>
      </div>
    );
  }

  if (errState === "notfound") {
    return <div className="min-h-screen flex items-center justify-center bg-[#FBF9F4]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="text-center">
        <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl font-light">Not found</div>
        <p className="mt-2 text-neutral-600">This invite link doesn&apos;t exist or has been removed.</p>
      </div>
    </div>;
  }

  if (!event) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;

  const palette = PALETTES.find((p) => p.name === event.palette) || PALETTES[0];
  const motif = themeMotifConfig(event.theme, event.custom_theme);
  const bannerUrl = event.banner_path ? publicImageUrl(event.banner_path) : null;
  const bannerPos = event.banner_position || "top";

  // wording overrides → fall back to defaults if blank
  const L = (v, d) => (v && String(v).trim()) ? v : d;
  const introText = L(event.intro_label, `You're invited to a ${event.event_type.replace("_", " ")}`);
  const hostedByText = L(event.hosted_by_label, "Hosted by");
  const countdownText = L(event.countdown_label, "The moment approaches");
  const whenText = L(event.when_label, "When");
  const whereText = L(event.where_label, "Where");
  const attireText = L(event.attire_label, "Attire");
  const aboutText = L(event.about_label, "About");
  const hostMsgText = L(event.host_message_label, "A word from your host");
  const mapText = L(event.map_label, "Find your way");
  const rsvpIntroText = L(event.rsvp_intro_label, "Kindly respond");
  const rsvpTitleText = L(event.rsvp_title_label, "RSVP");

  let dateStr = ""; let timeStr = "";
  try { const d = new Date(event.event_datetime); dateStr = format(d, "EEEE, MMMM d, yyyy"); timeStr = format(d, "p"); } catch {}

  const bannerEl = bannerUrl ? (
    <div data-testid="public-banner" className="w-full h-64 sm:h-80 overflow-hidden">
      <img src={bannerUrl} alt="" className="w-full h-full object-cover" />
    </div>
  ) : null;

  const topBandEl = event.motif_density === "busy" ? (
    <div className={`w-full ${bannerPos === "after_band" && bannerUrl ? "" : "-mb-2"}`}>
      <ThemeBandImage theme={event.theme} customTheme={event.custom_theme} eventType={event.event_type} variant="top" />
    </div>
  ) : null;

  return (
    <div className="min-h-screen" style={{ background: palette.bg, color: palette.primary, fontFamily: "'Manrope', sans-serif" }}>
      {referrerName && (
        <div data-testid="referrer-banner" className="text-center py-3 text-[10px] tracking-[0.3em] uppercase opacity-80" style={{ background: `${palette.primary}10` }}>
          You were invited by {referrerName}
        </div>
      )}
      {bannerPos === "top" && bannerEl}
      {topBandEl}
      {bannerPos === "after_band" && bannerEl}
      <section className="relative" id="invite-hero">
        <div className={`max-w-5xl mx-auto px-6 lg:px-10 ${event.motif_density === "busy" ? "pt-4" : "pt-16 lg:pt-20"} pb-16 text-center`}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="text-[10px] tracking-[0.35em] uppercase opacity-70">{introText}</div>
            {event.show_theme_motif !== false && (
              <div className="mt-6"><ThemeOrnament config={motif} color={palette.primary} /></div>
            )}
            <h1 data-testid="public-title" style={{ fontFamily: "'Cormorant Garamond', serif" }} className="mt-8 text-5xl sm:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight font-light">
              {event.title}
            </h1>
            {event.show_host_name !== false && event.host_name && (
              <div className="mt-10 text-sm tracking-[0.2em] uppercase opacity-80" data-testid="public-host">{hostedByText} {event.host_name}</div>
            )}
          </motion.div>
        </div>
        <div className="max-w-4xl mx-auto px-6"><div className="h-px opacity-20" style={{ background: palette.primary }} /></div>
      </section>

      {event.show_countdown !== false && (
        <section className="py-16">
          <div className="text-center text-[10px] tracking-[0.35em] uppercase opacity-70 mb-8">{countdownText}</div>
          <CountdownTimer target={event.event_datetime} accent={palette.primary} />
        </section>
      )}

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6 border p-8" style={{ borderColor: `${palette.primary}22` }}>
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">{whenText}</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl sm:text-4xl mt-3 font-normal">{dateStr}</div>
            <div className="mt-2 text-lg opacity-80">{timeStr}</div>
          </div>
          <div className="col-span-12 md:col-span-6 border p-8" style={{ borderColor: `${palette.primary}22` }}>
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">{whereText}</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl sm:text-4xl mt-3 font-normal">{event.location_name}</div>
            <div className="mt-2 text-base opacity-80">{event.location_address}</div>
          </div>
          {event.show_dress_code !== false && event.dress_code && (
            <div className="col-span-12 md:col-span-6 border p-8" style={{ borderColor: `${palette.primary}22` }} data-testid="public-dress">
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">{attireText}</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl mt-3">{event.dress_code}</div>
            </div>
          )}
          {event.show_description !== false && event.description && (
            <div className="col-span-12 md:col-span-6 border p-8" style={{ borderColor: `${palette.primary}22` }} data-testid="public-description">
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">{aboutText}</div>
              <p className="mt-3 text-base leading-relaxed opacity-90 whitespace-pre-wrap">{event.description}</p>
            </div>
          )}
        </div>
      </section>

      {Array.isArray(event.custom_sections) && event.custom_sections.filter(s => (s?.title || "").trim() || (s?.body || "").trim()).length > 0 && (
        <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16" data-testid="public-custom-sections">
          <div className="grid grid-cols-12 gap-4">
            {event.custom_sections
              .filter(s => (s?.title || "").trim() || (s?.body || "").trim())
              .map((s, i) => (
                <div key={i} className="col-span-12 md:col-span-6 border p-8" style={{ borderColor: `${palette.primary}22` }} data-testid={`public-section-${i}`}>
                  {(s.title || "").trim() && (
                    <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">{s.title}</div>
                  )}
                  {(s.body || "").trim() && (
                    <p className="mt-3 text-base leading-relaxed opacity-90 whitespace-pre-wrap">{s.body}</p>
                  )}
                </div>
              ))}
          </div>
        </section>
      )}

      {event.show_host_message !== false && event.host_message && (
        <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-16 text-center" data-testid="public-host-message">
          <div className="text-[10px] tracking-[0.35em] uppercase opacity-70 mb-4">{hostMsgText}</div>
          <p style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl sm:text-3xl italic font-light leading-relaxed">
            &ldquo;{event.host_message}&rdquo;
          </p>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
        <div className="text-[10px] tracking-[0.35em] uppercase opacity-70 mb-4 text-center">{mapText}</div>
        <MapEmbed address={event.location_address} name={event.location_name} />
      </section>

      <section className="max-w-2xl mx-auto px-6 lg:px-10 pb-24">
        <div className="text-center mb-8">
          <div className="text-[10px] tracking-[0.35em] uppercase opacity-70">{rsvpIntroText}</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl sm:text-5xl mt-3 font-light tracking-tight">{rsvpTitleText}</h2>
          {event.rsvp_deadline && <div className="mt-2 text-sm opacity-70">By {format(new Date(event.rsvp_deadline), "MMMM d, yyyy")}</div>}
        </div>
        <div className="border p-6 sm:p-10 bg-white/60" style={{ borderColor: `${palette.primary}22` }}>
          <RSVPFormRenderer event={event} accent={palette.primary} refToken={refToken} />
        </div>
      </section>

      {event.motif_density === "busy" && (
        <div className="w-full mt-4">
          <ThemeBandImage theme={event.theme} customTheme={event.custom_theme} eventType={event.event_type} variant="bottom" />
        </div>
      )}

      <footer className="py-8 text-center text-[10px] tracking-[0.3em] uppercase opacity-60">
        {event.enable_guestbook && (
          <div className="mb-4">
            <Link to={`/i/${event.slug}/guestbook`} data-testid="guestbook-link" className="inline-flex items-center gap-2 border px-4 py-2 hover:bg-black/5" style={{ borderColor: `${palette.primary}33` }}>
              <BookOpen size={12} /> Visit the memory wall
            </Link>
          </div>
        )}
        Invitation by <a href="/" className="underline underline-offset-4">Soirée</a>
      </footer>
    </div>
  );
}
